import React, { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { Card, Col, Row, Badge, Accordion } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { useGetContractDetailsQuery } from "../../../app/features/apiSlice";
import Loader from "../../../components/Loader";
import Error from "../../../components/Error";
import { setPageTitle } from "../../../app/features/DataSlice";
import CurrencyValue from "../../../components/CurrencyValue";
import ContractDetailTable from "../../../components/HeliorPlan/ContractDetailTable";
// import UpdateInitialIngot from "../../../components/UpdateInitialIngot";
import moment from 'moment'

const ContractDetail = () => {
  const pageTitle = "XPlan";
  const { t } = useTranslation();
  const { contractCode } = useParams();
  const dispatch = useDispatch();
  const sessionId = useRef(Date.now()).current;

  const { data, isLoading, isError, error, isSuccess } =
    useGetContractDetailsQuery({ sessionId, contractCode });

  const contractCurrency = data?.data?.currency;
  const contractRate = data?.data?.fx_rate;

  useEffect(() => {
    dispatch(setPageTitle(pageTitle));
  });

  const renderContractDetails = () => (
    <Row>
      <Col xs="12" md="4">
        <ul className="list-unstyled m-0">
          <li className="my-2  d-flex justify-content-between align-items-center">
            <span className="lh-sm">{t("date")}:</span>{" "}
            {data?.data?.contract_date
              ? moment(data?.data?.contract_date).format("DD-MM-YYYY")
              : ""}
          </li>

          <li className="my-2 d-flex justify-content-between align-items-center">
            <span className="lh-sm">{t("periodicity")}:</span>{" "}
            {t(`${data?.data?.periodicity_of_amount}`)}
          </li>

          <li className="my-2 d-flex justify-content-between align-items-center">
            <span className="w-50 text-break lh-sm">{t("paymentAmount")}:</span>{" "}
            <span className="w-50 text-end text-break">
              <CurrencyValue
                currency={contractCurrency}
                rate={contractRate}
                amount={data?.data?.payment_amount}
              />
            </span>
          </li>

          <li className="my-2 d-flex justify-content-between align-items-center">
            <span className="lh-sm">{t("initialPayment")}:</span>{" "}
            {data?.data?.number_of_initial_payments}
          </li>
        </ul>
      </Col>

      <Col xs="12" md="4">
        <ul className="list-unstyled m-0">
          <li className="my-2  d-flex justify-content-between align-items-center">
            <span className="w-50 text-break lh-sm">{t("initialIngot")}:</span>{" "}
            {data?.data?.initial_ingot_gr} gr
          </li>

          <li className="my-2  d-flex justify-content-between align-items-center">
            <span className="w-50 text-break lh-sm">{t("recurringPayments")}:</span>{" "}
            {data?.data?.number_of_recurring_payments}
          </li>

          <li className="my-2 d-flex justify-content-between align-items-center">
            <span className="w-50 text-break lh-sm">{t("recurringIngot")}:</span>{" "}
            {data?.data?.ingot_gr} gr
          </li>

          <li className="my-2 d-flex justify-content-between align-items-center">
            <span className="w-50 text-break lh-sm">{t("totalPayment2")}:</span>{" "}
            <span className="w-50 text-end text-break">
              <CurrencyValue
                currency={contractCurrency}
                rate={contractRate}
                amount={data?.data?.recurring_amount_paid}
              />
            </span>
          </li>
        </ul>
      </Col>

      <Col xs="12" md="4">
        <ul className="list-unstyled m-0">
          <li className="my-2 d-flex justify-content-between align-items-center">
            <span className="w-50 text-break lh-sm">{t("totalPurchaseWithTX")}:</span>{" "}
              <span className="w-50 text-end text-break">
            <CurrencyValue
              currency={contractCurrency}
              rate={contractRate}
              amount={
                data?.data?.recurring_amount_paid +
                data?.data?.recurring_amount_paid * 0.005
              }
            />
            </span>
          </li>

          <li className="my-2 d-flex justify-content-between align-items-center">
            <span className="w-50 text-break lh-sm">{t("quotaDoc")}:</span>{" "}
            <span className="w-50 text-end text-break">
              <CurrencyValue
                currency={contractCurrency}
                rate={contractRate}
                amount={data?.data?.doc_share}
              />
            </span>
          </li>

          <li className="my-2 d-flex justify-content-between align-items-center">
            <span className="w-50 text-break lh-sm">{t("recurringAmountPaid")}:</span>{" "}
            <span className="w-50 text-end text-break">
              <CurrencyValue
                currency={contractCurrency}
                rate={contractRate}
                amount={data?.data?.total_payment}
              />
            </span>
          </li>

          {data?.data?.disc_code && (
            <li className="my-2 d-flex justify-content-between align-items-center text-gold">
              <span className="w-50 text-break lh-sm">{t("couponCode")}:</span> {data?.data?.disc_code}
            </li>
          )}
        </ul>
      </Col>
    </Row>
  );

  const renderContractInfo = (isMobile = false) => (
    <div className={`d-flex align-items-center ${isMobile ? "" : "h-100 justify-content-lg-end"}`}>
      <i className="fa fa-cart-shopping bg-gold text-light p-3 rounded-circle font-40" />
      <div className="m-3 text-start">
        <h6 className="mb-0 text-light">{contractCode}</h6>
        {data?.data?.docSplitRequest === "Pending" ? (
          <p className="text-light mb-0">
            {t("docSplitRequest")}: {data?.data?.docSplitRequest}
          </p>
        ) : (
          <Badge
            bg={`${data?.data?.status === "Active" ? "success" : "danger"}`}
          >
            {data?.data?.status === "Active"
              ? t("active")
              : t("inactive")}
          </Badge>
        )}
      </div>
    </div>
  );

  return (
    <div className="my-3">
      {isLoading && <Loader />}
      {isError && <Error error={error} />}
      {isSuccess && (
        <>
          {/* Desktop View */}
          <Card className="d-none d-lg-block mb-2" bg="dark1 text-light">
            <Card.Body>
              <Row className="contract-row">
                <Col xs="12" lg="9" className="contract-details">
                  {renderContractDetails()}
                </Col>
                <Col xs="12" lg="3" className="contract-info">
                  {renderContractInfo(false)}
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Mobile Accordion View */}
          <Accordion className="d-lg-none mb-2 contract-accordion">
            <Accordion.Item eventKey="0">
              <Accordion.Header>
                {renderContractInfo(true)}
              </Accordion.Header>
              <Accordion.Body>
                {renderContractDetails()}
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
        </>
      )}

      <ContractDetailTable payments={data?.data?.payment} currency={contractCurrency} rate={contractRate} />
    </div>
  );
};

export default ContractDetail;